/**
 * Centralized Frontend Configuration
 * Single source of truth for all frontend configuration values
 */

import { PORTS, CONFIG as PORT_CONFIG } from './ports';

/**
 * Main application configuration
 */
export const CONFIG = {
  // API endpoints
  api: {
    backend: PORT_CONFIG.getBackendUrl(),
    baseUrl: PORT_CONFIG.getApiBaseUrl(),
    paperTrading: PORT_CONFIG.getPaperTradingApiUrl(),
    backtest: PORT_CONFIG.getBacktestApiUrl(),
    tradovate: import.meta.env.VITE_TRADOVATE_URL || 'http://localhost:9548',
  },

  // Dynamic URL getters (delegate to PORT_CONFIG)
  getBackendUrl: (hostname?: string) => PORT_CONFIG.getBackendUrl(hostname),
  getApiBaseUrl: (hostname?: string) => PORT_CONFIG.getApiBaseUrl(hostname),

  // WebSocket configuration
  websocket: {
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    timeout: 20000,
  },

  // Trading defaults - these are generic, actual values come from instruments.ts based on symbol
  trading: {
    defaultSymbol: 'MNQU6',
    defaultQuantity: 1,
    defaultStopLossPoints: 25,
    defaultTakeProfitPoints: 5,
    tickSize: 0.25, // Default tick size (MNQ/NQ/ES/MES all use 0.25)
    tickValue: 0.5,  // Default tick value (use getInstrumentSpec for actual values)
  },

  // UI defaults
  ui: {
    debounceMs: 500,
    animationDuration: 200,
    toastDuration: 3000,
    maxLogEntries: 1000,
  },

  // Chart defaults
  chart: {
    defaultTimeframe: '2m',
    candleColors: {
      up: '#26a69a',
      down: '#ef5350',
      upWick: '#26a69a',
      downWick: '#ef5350',
      upBorder: '#26a69a',
      downBorder: '#ef5350',
    },
    volumeColors: {
      up: 'rgba(38, 166, 154, 0.5)',
      down: 'rgba(239, 83, 80, 0.5)',
    },
    gridColor: 'rgba(197, 203, 206, 0.1)',
    textColor: '#D1D4DC',
    backgroundColor: '#1E222D',
    defaultVisibleBars: 100,
    minVisibleBars: 10,
    maxVisibleBars: 500,
  },

  // Strategy defaults
  strategy: {
    wickThresholdMin: 0,
    wickThresholdMax: 25,
    skipIfHOD: false,
    skipIfLOD: false,
    hodLodRangePoints: 5,
    skipIfLowATR: true,
    atrMinThreshold: 7,
    skipIfSmallCandle: true,
    minCandleLengthPoints: 7,
    sessionStartHour: 21,
    sessionEndHour: 22,
    predictorEnabled: true,
    predictorAlertThreshold: 70,
    predictorStartTrackingAt: 80,
  },

  // Data configuration
  data: {
    historicalBarsToLoad: 3900, // ~3 trading days at 2min intervals
    maxCandlesInMemory: 10000,
    backtestSpeedOptions: [1, 5, 10, 25, 50, 100],
    defaultBacktestSpeed: 10,
  },

  // Predictor configuration
  predictor: {
    updateIntervalMs: 1000,
    alertThresholdPercent: 70,
    trackingStartSeconds: 80,
    minConfidencePercent: 50,
  },

  // Ports (re-export for backwards compatibility)
  ports: PORTS,
} as const;

/**
 * Environment helpers
 */
export const ENV = {
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  mode: import.meta.env.MODE,
} as const;

/**
 * Feature flags
 */
export const FEATURES = {
  enableLiveTrading: true,
  enableBacktesting: true,
  enablePaperTrading: true,
  enableStrategySettings: true,
  enableSignalPredictor: true,
  enableFibLevels: true,
  enablePositionControls: true,
} as const;

// Export individual configs for convenience
export { PORTS } from './ports';
// Timeframe type defined locally since it's not in shared types
export type Timeframe = '1m' | '2m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d';

// Default export
export default CONFIG;
